import classNames from 'classnames';
import ActionButton from 'components/ActionButton';
import Icon from 'components/Icon';
import Portal from 'components/Portal';
import { DropdownContext } from 'hocs/withContext';
import withIsVisible from 'hocs/withIsVisible';
import withPortalPosition from 'hocs/withPortalPosition';
import { listenClick } from 'lib/utils';
import PropTypes from 'prop-types';
import React, { Component, forwardRef } from 'react';
import nextId from 'react-id-generator';
import { compose } from 'redux';
import './style.scss';

class DropdownPortal extends Component {
  static propTypes = {
    title: PropTypes.any,
    className: PropTypes.string,
    iconSize: PropTypes.number,
    onClear: PropTypes.func,
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    onClick: PropTypes.func,
    tabIndex: PropTypes.number,
    disableDropdown: PropTypes.bool,
    allowUpward: PropTypes.bool,
    'data-cy': PropTypes.string,
    'data-testid': PropTypes.string
  };

  static defaultProps = {
    iconSize: 14,
    allowUpward: false
  };

  static currentlyOpenDropdown = null;

  // Used to position the portal and detect outside clicks
  containerRef = React.createRef();

  constructor() {
    super();

    this.state = {
      isOpen: false,
      isOpeningUpward: false
    };

    this.listenClick = this.listenClick.bind(this);

    // Used to traverse DOM from portal to container
    this.id = nextId();
  }

  // Add or remove listener that closes the dropdown on click
  componentDidUpdate(prevProps, prevState) {
    if (!prevState.isOpen && this.state.isOpen) {
      setTimeout(() => document.addEventListener('click', this.listenClick), 250);
    }

    if (prevState.isOpen && !this.state.isOpen) {
      document.removeEventListener('click', this.listenClick);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.listenClick);
  }

  getDropdownContext() {
    return {
      toggle: this.toggle,
      isOpeningUpward: this.state.isOpeningUpward
    };
  }

  listenClick(e) {
    if (this.containerRef.current) {
      listenClick(e, this.containerRef.current, () => this.toggle(false));
    }
  }

  handleClick = e => {
    if (this.props.disabled) {
      return;
    }

    const { onClick } = this.props;
    const { isOpen } = this.state;

    if (onClick) {
      onClick(e);
    }

    this.toggle(!isOpen);
  };

  onInputKeyDown = e => {
    const { isOpen } = this.state;
    let handled = true;

    if (e.code === 'Space') {
      this.toggle(!isOpen);
    } else if (e.code === 'Escape' && isOpen) {
      this.toggle(false);
    } else {
      handled = false;
    }

    if (handled) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  handleBlur = e => {
    if (this.props.tabIndex === undefined || e.relatedTarget === null) {
      return;
    }

    this.toggle(false);
  };

  toggle = isOpen => {
    const nextIsOpen = typeof isOpen === 'boolean' ? isOpen : !this.state.isOpen;

    if (nextIsOpen && DropdownPortal.currentlyOpenDropdown && DropdownPortal.currentlyOpenDropdown !== this) {
      DropdownPortal.currentlyOpenDropdown.toggle(false);
    }

    DropdownPortal.currentlyOpenDropdown = nextIsOpen ? this : null;

    // Calculate upward state when opening
    let shouldOpenUpward = false;
    if (nextIsOpen && this.containerRef.current && this.props.allowUpward) {
      const rect = this.containerRef.current.getBoundingClientRect();
      const distanceFromBottom = window.innerHeight - rect.bottom;
      shouldOpenUpward = distanceFromBottom < 200;
    }

    this.props.setGetNextPortalPosition(nextIsOpen ? this.setPortalPosition : null);

    this.setState(
      {
        isOpen: nextIsOpen,
        isOpeningUpward: nextIsOpen ? shouldOpenUpward : false
      },
      () => {
        if (this.state.isOpen === false && this.props.onClose) {
          this.props.onClose();
        }

        if (this.state.isOpen && this.props.onOpen) {
          this.props.onOpen();
        }
      }
    );
  };

  setPortalPosition = () => {
    if (!this.containerRef.current) {
      return;
    }

    const rect = this.containerRef.current.getBoundingClientRect();
    const position = {};

    position.left = rect.left;
    position.width = rect.width;

    // Check if we're within 200px of the bottom of the viewport and allowUpward is enabled
    const distanceFromBottom = window.innerHeight - rect.bottom;
    const shouldOpenUpward = this.props.allowUpward && distanceFromBottom < 200;

    if (shouldOpenUpward) {
      // Position so dropdown's bottom aligns with top of trigger
      position.bottom = window.innerHeight - (rect.top + window.scrollY);
      position.top = 'auto';
    } else {
      // Position dropdown below the trigger element (default behavior)
      position.top = rect.top + rect.height + window.scrollY;
    }

    if (!this.props.isVisible) {
      position.display = 'none';
    }

    return position;
  };

  render() {
    const {
      title,
      className,
      portalClassName,
      disabled,
      children,
      onClear,
      tabIndex,
      iconSize,
      portalPosition,
      innerRef,
      forwardedRef,
      disableDropdown,
      'data-cy': dataCY,
      'data-testid': dataTestId
    } = this.props;

    const dropdownCN = classNames({
      'dropdown-portal': true,
      'dropdown-portal--open': this.state.isOpen,
      'dropdown-portal--upward': this.state.isOpeningUpward,
      'dropdown-portal--disabled': disabled,
      [className]: Boolean(className)
    });

    const portalCN = classNames({
      'dropdown-portal-body': true,
      'dropdown-portal-body--open': this.state.isOpen,
      'dropdown-portal-body--upward': this.state.isOpeningUpward,
      [portalClassName]: Boolean(portalClassName)
    });

    const clearButtonCN = classNames('dropdown-portal__header-clear', {
      'dropdown-portal__header-clear--hidden': !onClear
    });

    const containerCN = classNames('dropdown-portal__header', {
      'dropdown-portal__header--disabled-dropdown': disableDropdown
    });

    return (
      <DropdownContext.Provider value={this.getDropdownContext()}>
        <div
          id={this.id}
          className={dropdownCN}
          ref={el => {
            this.containerRef.current = el;
            innerRef.current = el; // withIsVisible
            forwardedRef.current = el;
          }}
          data-cy={dataCY}
          data-testid={dataTestId}
        >
          <div
            className={containerCN}
            tabIndex={tabIndex}
            onClick={this.handleClick}
            onBlur={this.handleBlur}
            onKeyDown={this.onInputKeyDown}
          >
            <div className="dropdown-portal__header-title">{title}</div>

            <Icon className="dropdown-portal__header-arrow" name="chevron-down" size={iconSize} />
          </div>

          <ActionButton className={clearButtonCN} iconName="clear-circle" size={iconSize} onClick={onClear} />

          <Portal>
            <div className={portalCN} style={{ ...portalPosition }} data-portal-for={this.id}>
              {children}
            </div>
          </Portal>
        </div>
      </DropdownContext.Provider>
    );
  }
}

const enhance = compose(withPortalPosition, withIsVisible);
const EnhancedDropdownPortal = enhance(DropdownPortal);

export default forwardRef((props, ref) => <EnhancedDropdownPortal {...props} forwardedRef={ref} />);
